import { CalendarWeek } from '@components/ui';
import { useOnInterval } from '@components/ui/calendar/calendar-utils';
import { WCS } from '@components/ui/calendar/calendar-week/week-utils';
import {
  ICalendarEvent,
  IMarkDate,
  IMarkedEvents,
  IMenuRenderInfo,
  IRenderInfo,
  WeekCalendarHandle,
} from '@components/ui/calendar/calendars';
import { actions as eVisitActions } from '@store/ducks/e-visits';
import {
  endOfDay,
  endOfWeek,
  format,
  getISOWeek,
  isAfter,
  parseISO,
  setDay,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EVisitItem, TDay, TDays, TExistingEVisitRule } from 'types/e-visits';
import TStore from 'types/store';
import { TUserMedicalInstitution } from 'types/users';
import ReservationForm from '../reservation-form/reservation-form';
import { connectReservationForm, mapEvents } from './calendar-view.utils';
import CalendarMenu from './components/calendar-menu';
import CancelVisitModal from './components/cancel-visit-modal';
import CustomOrderAddEventItem from './components/custom-order-add-event-item';
import EventItem from './components/event-item';

const WEEK_STARTS_ON = Number(process.env.REACT_APP_WEEK_STARTS_ON) as TDay;

interface CalendarViewProps {
  selectedInstitution: TUserMedicalInstitution | undefined;
  userId: string;
  selectedDate: Date;
  setSelectedDate: (d: Date) => void;

  canAddEvent?: boolean;
}

const CalendarView: React.FunctionComponent<CalendarViewProps> = ({
  selectedInstitution,
  userId,
  selectedDate,
  setSelectedDate,
  canAddEvent = true,
}) => {
  const dispatch = useDispatch();

  const officeHours = useSelector((state: TStore) => state.eVisits.officeHours);
  const calendarEvents = useSelector((state: TStore) => state.eVisits.events);
  const [deleteModal, setDeleteModal] = React.useState<{ visible: boolean; event?: EVisitItem }>({
    visible: false,
  });
  const loading = useSelector(
    (state: TStore) =>
      state.eVisits.getEVisitsEventsStatus === 'pending' ||
      state.eVisits.getOfficeHoursStatus === 'pending' ||
      state.reservationForm.formState === 'submitting',
  );

  const fetch = React.useCallback(() => {
    if (selectedInstitution) {
      dispatch(
        eVisitActions.getEventsRequest({
          fromDate: startOfDay(startOfWeek(selectedDate, { weekStartsOn: WEEK_STARTS_ON })),
          toDate: endOfDay(endOfWeek(selectedDate, { weekStartsOn: WEEK_STARTS_ON })),
          expertiseCode: selectedInstitution.expertise?.code ?? '',
          medicalInstitutionId: selectedInstitution.id,
        }),
      );
      dispatch(
        eVisitActions.getOfficeHoursRequest({
          doctorUserId: userId,
          expertiseCode: selectedInstitution.expertise?.code ?? '',
          medicalInstitutionId: selectedInstitution.id,
          ISOWeekNumber: getISOWeek(
            startOfWeek(selectedDate, {
              weekStartsOn: WEEK_STARTS_ON,
            }),
          ),
          year: selectedDate.getFullYear().toString(),
        }),
      );
    }
  }, [dispatch, userId, selectedInstitution, selectedDate]);

  const cRef = React.useRef<WeekCalendarHandle<any>>(null);
  connectReservationForm(fetch, cRef);

  const handleDelete = React.useCallback((item: EVisitItem) => {
    setDeleteModal({
      visible: true,
      event: item,
    });
  }, []);

  const closeModal = React.useCallback(() => {
    setDeleteModal({ visible: false });
  }, []);

  const _renderMenu = React.useCallback(
    ({ event, onClose, direction }: IMenuRenderInfo<EVisitItem>) => {
      const ordered = !!event.eventData?.order;
      const free = !!event.eventData?.event;

      if (!(ordered && free)) {
        return (
          <ReservationForm
            mode={free ? 'add' : 'create'}
            onClose={() => {
              onClose(true);
            }}
            direction={direction}
            handleDeleteClick={() => {
              throw new Error('NOT IMPLEMENTED ON CREATE FORM');
            }}
          />
        );
      }

      return (
        <CalendarMenu
          item={event.eventData!}
          onClose={onClose}
          onDelete={handleDelete}
          horizontalPosition={direction?.horizontalPosition}
        />
      );
    },
    [handleDelete],
  );

  const _renderEvent = React.useCallback(
    ({ item, onClick }: IRenderInfo<EVisitItem>) => (
      <EventItem item={item.eventData!} onClick={onClick} />
    ),
    [],
  );

  useOnInterval(60000, fetch);

  const marked = React.useMemo(() => {
    const result: IMarkedEvents = {};

    if (!officeHours) {
      return result;
    }

    const start = startOfWeek(selectedDate, {
      weekStartsOn: WEEK_STARTS_ON,
    });

    Object.keys(officeHours || {}).forEach((k) => {
      const list: IMarkDate[] = officeHours[k as TDays]!.map((d) => ({
        color: '#F7F7F9',
        end: d.to,
        start: d.from,
      }));

      const forDate = format(
        setDay(start, Number(k), {
          weekStartsOn: 0,
        }),
        'yyyy-MM-dd',
      );

      result[forDate] = list;
    });

    return result;
  }, [officeHours, selectedDate]);

  const mappedEvents = React.useMemo(() => mapEvents(calendarEvents), [calendarEvents]);

  const [editing, setEditing] = React.useState(false);
  const _onMenuClose = React.useCallback(() => {
    setEditing(false);
    return false;
  }, []);

  const _onMenuOpen = React.useCallback(
    (event: ICalendarEvent<EVisitItem>) => {
      if (editing) {
        return false;
      }

      const ordered = !!event.eventData?.order;
      const free = !!event.eventData?.event;
      const isEmptySlot = !ordered && free;

      if (isEmptySlot) {
        // @ts-ignore
        const isInFuture = isAfter(parseISO(event.eventData?.event.to), Date.now());
        if (isInFuture) {
          setEditing(true);
          return true;
        }
      } else if (ordered) {
        setEditing(true);
        return true;
      }

      return false;
    },
    [editing],
  );

  const _renderAddEvent = React.useCallback((event: ICalendarEvent<TExistingEVisitRule>) => {
    return <CustomOrderAddEventItem item={event} />;
  }, []);

  const onCancelEntrySuccess = React.useCallback(() => {
    _onMenuClose();
    if (cRef.current) {
      cRef.current.closeMenu(true);
    }
    fetch();
  }, [_onMenuClose, fetch, cRef]);

  return (
    <>
      <CalendarWeek
        ref={cRef}
        onMenuOpen={_onMenuOpen}
        onMenuClose={_onMenuClose}
        events={mappedEvents}
        markedEvents={marked}
        renderEvent={_renderEvent}
        renderMenu={_renderMenu}
        displayAllDay
        selectedDate={format(selectedDate, 'yyyy-MM-dd')}
        onChange={setSelectedDate}
        hoursPadding={1}
        showDayProgress
        weekStartsOn={WEEK_STARTS_ON}
        loading={loading}
        renderAddEvent={_renderAddEvent}
        selectedInstitution={selectedInstitution}
        menuWidth={WCS.menuWidthCalendarView}
        mode={'view'}
        disableAdd={!canAddEvent}
      />
      <CancelVisitModal
        visible={deleteModal.visible}
        onClose={closeModal}
        event={deleteModal.event}
        onSuccess={onCancelEntrySuccess}
      />
    </>
  );
};

export default React.memo(CalendarView);
